// Contact — one form, and the first field asks which door you came through.
// That answer routes the notification to the right inbox and is stored on the
// message, so the admin Contact Messages tab shows it too.

import React, { useEffect, useState } from 'react';
import { Bay, Btn, Card, Eyebrow, Head } from '../primitives.jsx';
import { STUDIO } from '../content.js';

const API_BASE = import.meta.env.VITE_API_URL || '';

const PATHS = [
  { id: 'content', label: 'I need content made', hint: 'Video and photo for your social, on retainer: local businesses, brands, buildings.' },
  { id: 'studio', label: 'I want to book the room', hint: 'Studio rental, podcast session, event or something else in the space.' },
  { id: 'other', label: 'Something else', hint: 'Press, partnerships, working together, saying hello.' },
];

function initialPath() {
  if (typeof window === 'undefined') return 'content';
  const q = new URLSearchParams(window.location.search).get('path');
  return PATHS.some((p) => p.id === q) ? q : 'content';
}

export default function Contact() {
  const [path, setPath] = useState(initialPath);
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [state, setState] = useState({ status: 'idle', error: '' });

  useEffect(() => { setPath(initialPath()); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (state.status === 'sending') return;
    setState({ status: 'sending', error: '' });
    try {
      const chosen = PATHS.find((p) => p.id === path);
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          audience: path,
          // Keep the subject line useful in the inbox even if they left it blank.
          subject: form.subject || chosen.label,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'That did not go through.');
      setState({ status: 'sent', error: '' });
    } catch (err) {
      setState({ status: 'error', error: err.message || 'That did not go through.' });
    }
  };

  if (state.status === 'sent') {
    return (
      <Bay>
        <Card>
          <Eyebrow>Got it</Eyebrow>
          <h1 className="hddn-h2" style={{ marginTop: 14 }}>Message received.</h1>
          <p className="hddn-lede" style={{ marginTop: 16, maxWidth: '48ch' }}>
            It went to the right inbox and someone here will come back to you. If it is urgent,
            email <a href={`mailto:${STUDIO.email}`} style={{ color: 'var(--accent-ink)', textDecoration: 'underline' }}>{STUDIO.email}</a> directly.
          </p>
          <div style={{ marginTop: 26, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Btn href="/work" variant="line">See the work</Btn>
            <Btn href="/studio" variant="line">See the studio</Btn>
          </div>
        </Card>
      </Bay>
    );
  }

  return (
    <>
      <Bay band="ground" tight>
        <Eyebrow>Contact</Eyebrow>
        <h1 className="hddn-h1" style={{ marginTop: 18, maxWidth: '16ch' }}>Which door are you coming through?</h1>
        <p className="hddn-lede" style={{ marginTop: 22, maxWidth: '46ch' }}>
          Tell us that first and the rest is three fields. Booking a photographer? That has{' '}
          <a href="/photographer" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>its own form</a>.
        </p>
      </Bay>

      <Bay>
        <div className="hddn-grid hddn-grid--sidebar">
          <form onSubmit={submit} noValidate>
            <fieldset style={{ border: 0, padding: 0, margin: '0 0 26px' }}>
              <legend className="hddn-label" style={{ marginBottom: 12 }}>I am here because…</legend>
              <div style={{ display: 'grid', gap: 10 }}>
                {PATHS.map((p) => (
                  <label
                    key={p.id}
                    className="hddn-card"
                    style={{
                      display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer',
                      padding: '16px 18px',
                      borderColor: path === p.id ? 'var(--accent)' : undefined,
                      borderWidth: path === p.id ? 2 : 1,
                    }}
                  >
                    <input
                      type="radio"
                      name="path"
                      value={p.id}
                      checked={path === p.id}
                      onChange={() => setPath(p.id)}
                      style={{ marginTop: 4, accentColor: 'var(--accent-ink)' }}
                    />
                    <span>
                      <span style={{ fontWeight: 700, display: 'block' }}>{p.label}</span>
                      <span className="hddn-note">{p.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="hddn-field">
              <span>Name <abbr title="required" style={{ color: 'var(--accent-ink)', textDecoration: 'none' }}>*</abbr></span>
              <input className="hddn-input" required value={form.name} onChange={set('name')} autoComplete="name" />
            </label>
            <label className="hddn-field">
              <span>Email <abbr title="required" style={{ color: 'var(--accent-ink)', textDecoration: 'none' }}>*</abbr></span>
              <input className="hddn-input" type="email" required value={form.email} onChange={set('email')} autoComplete="email" />
            </label>
            <label className="hddn-field">
              <span>Phone <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span></span>
              <input className="hddn-input" type="tel" value={form.phone} onChange={set('phone')} autoComplete="tel" />
            </label>
            <label className="hddn-field">
              <span>{path === 'studio' ? 'What are you shooting?' : 'Subject'}</span>
              <input
                className="hddn-input"
                value={form.subject}
                onChange={set('subject')}
                placeholder={path === 'content' ? 'e.g. New cafe on Main Street, or a 120-unit lease-up' : path === 'studio' ? 'e.g. Podcast, 2 hours, weekday evening' : ''}
              />
            </label>
            <label className="hddn-field">
              <span>Message <abbr title="required" style={{ color: 'var(--accent-ink)', textDecoration: 'none' }}>*</abbr></span>
              <textarea className="hddn-input" required rows={6} value={form.message} onChange={set('message')} />
            </label>

            {state.status === 'error' ? (
              <p className="hddn-alert" role="alert" style={{ marginBottom: 18, borderColor: 'var(--accent)' }}>{state.error}</p>
            ) : null}

            <Btn onClick={submit} variant="fill" disabled={state.status === 'sending'}>
              {state.status === 'sending' ? 'Sending…' : 'Send it'}
            </Btn>
            <p className="hddn-note" style={{ marginTop: 14 }}>
              Or just email <a href={`mailto:${STUDIO.email}`} style={{ color: 'var(--accent-ink)', textDecoration: 'underline' }}>{STUDIO.email}</a>.
            </p>
          </form>

          <Card>
            <p className="hddn-label">The studio</p>
            <address style={{ fontStyle: 'normal', marginTop: 14 }} className="hddn-h4">
              {STUDIO.street}<br />{STUDIO.city}, {STUDIO.state} {STUDIO.zip}
            </address>
            <p className="hddn-muted" style={{ marginTop: 14 }}>{STUDIO.hoursLabel}</p>
            <hr className="hddn-rule" style={{ margin: '22px 0' }} />
            <p className="hddn-note">
              Booking the room for a specific date is faster through the calendar — sessions
              48 hours out with payment confirm automatically.
            </p>
            <div style={{ marginTop: 18 }}>
              <Btn href="/book" variant="line" wide>Check availability</Btn>
            </div>
          </Card>
        </div>
      </Bay>
    </>
  );
}
